<template>
  <v-dialog
    v-model="dialog"
    persistent
    max-width="450px"
  >
    <template v-slot:activator="{ on }">
      <v-btn 
        icon 
        v-on="on"
      >
        <v-icon color="primary">
          mdi-pencil
        </v-icon>
      </v-btn>
    </template>
    <material-card
      :offset="10"
      :full-width="true"
      color="green"
      title="Training Session"
      text="Edit Training Sessions"
      class="mb-0"
    > 
      <form v-if="dialog">
        <v-container>
          <v-progress-linear
            v-if="loading"
            indeterminate
            color="primary darken-2"
          />
          <v-row v-if="!loading">
            <v-col
              cols="12"
              class="py-0"
            >
              <v-text-field
                v-model="name"
                v-validate="'required|max:255'"
                :error-messages="errors.collect('name')"
                data-vv-name="name"                
                label="Training Session Name"
              />
            </v-col>
            <v-col
              cols="12"
              class="py-0"
            >
              <v-menu
                id="date"
                v-model="datemenu"
                :close-on-content-click="false"
                :nudge-right="40"
                transition="scale-transition"
                offset-y
                min-width="290px"
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                    v-model="selectedDateDisplay"
                    v-validate="'required'"
                    label="Date"
                    prepend-icon="event"
                    :error-messages="errors.collect('date')" 
                    data-vv-name="date"
                    readonly
                    v-on="on"
                  />
                </template>
                <v-date-picker
                  v-model="date"
                  class="datepicker"
                  no-title
                  @input="datemenu = false"
                />
              </v-menu>    
            </v-col>

            <v-col
              cols="12"
              class="py-0"
            >
              <v-menu
                v-model="timeMenu"
                :close-on-content-click="false"
                :nudge-right="40"
                transition="scale-transition"
                offset-y
                min-width="290px"
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                    v-model="timeNow"
                    v-validate="'required'"
                    label="Time"
                    prepend-icon="mdi-clock"
                    :error-messages="errors.collect('time')"
                    data-vv-name="time"  
                    readonly
                    v-on="on"
                  />
                </template>

                <v-time-picker
                  ref="picker"
                  v-model="time"
                  :landscape="true"
                  width="190"
                  scrollable
                  @click:minute="timeMenu = false"
                />
              </v-menu>
            </v-col>

            <v-col
              cols="12"
              class="py-0"
            >
              <v-text-field
                v-model="venue"
                v-validate="'required|max:255'"
                :error-messages="errors.collect('venue')"
                data-vv-name="venue"                
                label="Venue"
              />
            </v-col>

            <v-col
              cols="12"
              class="py-0"
            >
              <v-text-field
                v-model="description"
                v-validate="'required'"
                :error-messages="errors.collect('description')"
                data-vv-name="description"                
                label="Description"
              />
            </v-col>

            <v-col
              v-if="session.instructor_uid"
              cols="12"
              class="py-0"
            >
              <v-autocomplete
                v-model="instructor"
                v-validate="'required'"
                :error-messages="errors.collect('instructor')"
                :items="instructors"
                data-vv-name="instructor"
                data-vv-as="instructor"
                item-text="person.name"
                item-value="employee_uid"
                label="Instructor"
                return-object
              />
            </v-col>

            <v-col
              v-else
              cols="12"
              class="py-0"
            >
              <v-text-field
                v-model="instructorName"
                v-validate="'required'"
                :error-messages="errors.collect('instructor name')"
                data-vv-name="instructor name"                
                label="Instructor Name"
              />
            </v-col>

            <v-col
              cols="12"
              class="py-0"
            >
              <v-autocomplete
                v-model="moderator"
                v-validate="'required'"
                :error-messages="errors.collect('moderator')"
                :items="moderators"
                data-vv-name="moderator"
                item-text="person.name"
                item-value="employee_uid"
                label="Moderator"
                return-object
              />
            </v-col>
          </v-row>
        </v-container>
      </form>
      
      <v-card-actions>
        <v-spacer />
        <v-btn
          :disabled="progressing"
          text
          @click="dialog = false"
        >
          Close
        </v-btn>
 
        <v-btn
          :loading="progressing"
          color="success"
          @click="submitForm"
        >
          Save Changes
        </v-btn>
      </v-card-actions>
    </material-card>
  </v-dialog>
</template>

<script>
  export default {
    inject: ['$validator'],
    props: {
      session: {
        type: Object,
        default: null,
      },
    },
 
    data () {
      return {
        progressing: false,
        dialog: null,
        description: null,
        instructors: [],
        instructor: null,
        datemenu: false,
        date: null,
        venue: null,
        time: null,
        moderators: [],
        moderator: null,
        instructorName: null,
        timeMenu: false,
        loading: true,
      }
    },

    computed: {
      selectedDateDisplay () {
        if (!this.date) return
        return this.$moment(this.date, 'YYYY-MM-DD').format('D MMM YYYY')
      },

      timeNow () {
        if (!this.time) return
        return this.formatTime(this.time)
      },
    },
    watch: {
      dialog: function (value) {
        if (!value) return
        this.resetForm()
        this.loadData()
      },
    },
    methods: {
      formatTime (time) {
        let a = (time || '').split(':') // split it at the colons
        let seconds = (a[0] * 60 * 60) + (a[1] * 60)

        return this.$moment(new Date(1970, 0, 1).setSeconds(seconds)).format('hh:mm A')
      },

      loadData () {
        Promise.all([
          this.$api.trainingSessionInstructors().list(),
          this.$api.trainingSessionModerators().list(),
        ])
          .then(([instructor, moderator]) => {
            this.instructors = instructor.data
            this.moderators = moderator.data
            
            this.session.instructor_uid ? this.instructor = this.session.instructor : this.instructorName = this.session.instructor_name
            this.description = this.session.description
            this.date = this.session.date
            this.venue = this.session.venue
            this.time = this.session.time
            this.moderator = this.session.moderator
            this.name = this.session.name
            this.loading = false
          })
      },

      submitForm () {
        this.$validator.validateAll().then(result => {
          if (!result) return

          this.progressing = true

          this.$api.trainingSessions().update(this.session.id, this.prepareFormData())
            .then(res => {
              if (res.status === 200) {
                this.$emit('updated')
                this.$snack({ message: 'Training Session Updated Succesfully' })

                this.dialog = false
              }
            })
            .catch(() => {})
            .then(() => {
              this.progressing = false
            })
        })
      },

      prepareFormData () {
        let data = {
          name: this.name,
          description: this.description,
          time: this.time,
          venue: this.venue,
          date: this.date,
          moderator_uid: this.moderator.employee_uid,
        }

        if (this.session.instructor) {
          Object.assign(data, { instructor_uid: this.instructor.employee_uid })
          Object.assign(data, { instructor_name: this.instructor.person.name })
        } else {
          Object.assign(data, { instructor_name: this.instructorName })
        }

        return data
      },

      resetForm () {
        this.name = null
        this.description = null
        this.time = null
        this.venue = null
        this.$validator.reset()
      },
    },
  }
</script>
